
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Mail, AlertCircle, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { emailConfig } from "@/lib/env";

interface EmailNotReceivedProps {
  email?: string;
  onSubmit?: (email: string) => void;
}

const EmailNotReceived: React.FC<EmailNotReceivedProps> = ({ 
  email: initialEmail = "", 
  onSubmit 
}) => {
  const [email, setEmail] = useState<string>(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { toast } = useToast();

  const handleResendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log("Sending email to:", email);
      console.log("Using API endpoint:", emailConfig.apiEndpoint);
      
      // Send email using Mailtrap API
      const response = await fetch(emailConfig.apiEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${emailConfig.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: {
            email: emailConfig.email,
            name: emailConfig.fullName
          },
          to: [
            {
              email: email
            }
          ],
          subject: "Verify your email address",
          text: "Thank you for registering! Please verify your email address to complete your registration.",
          html: "<p>Thank you for registering!</p><p>Please verify your email address to complete your registration.</p>",
          category: "Verification"
        })
      });

      console.log("Response status:", response.status);
      
      if (response.ok) {
        if (onSubmit) {
          onSubmit(email);
        }
        
        toast({
          title: "Verification email sent",
          description: `We've sent another verification email to ${email}. Please check your inbox and spam folders.`,
        });
      } else {
        const errorText = await response.text();
        console.error("Email API error:", errorText);
        throw new Error(`Failed to send email: ${response.statusText} (${errorText})`);
      }
    } catch (error) {
      console.error("Failed to resend verification email:", error);
      toast({
        title: "Failed to resend",
        description: "There was a problem sending the verification email. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border shadow-md max-w-md mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          <CardTitle className="text-xl">Didn't receive an email?</CardTitle>
        </div>
        <CardDescription>
          If you haven't received your verification email, we can send it again to your inbox.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleResendEmail} className="space-y-4">
          <div className="space-y-2">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Resend Verification Email"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span>Emails are usually delivered within a minute</span>
        </div>
        <div className="mt-2 text-xs">
          Sending from: {emailConfig.fullName} &lt;{emailConfig.email}&gt;
        </div>
      </CardFooter>
    </Card>
  );
};

export default EmailNotReceived;
